import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { TimePicker } from './TimePicker';
import { cn } from '../lib/utils';

const pad = (n: number) => n.toString().padStart(2, '0');

interface DateTimePickerProps {
  /** epoch milliseconds, or null when unset */
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  /** Predicate to disable specific days (e.g. past dates). */
  disabled?: (date: Date) => boolean;
}

/**
 * A single date + time picker: a calendar popover (shadcn Calendar) combined
 * with the existing TimePicker. Value is exchanged as epoch milliseconds.
 */
export function DateTimePicker({ value, onChange, placeholder = 'Pick a date', disabled }: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const date = value !== null ? new Date(value) : undefined;
  const timeValue = date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : '00:00';

  const handleDateSelect = (selected?: Date) => {
    if (!selected) {
      onChange(null);
      return;
    }
    const next = new Date(selected);
    // Preserve the previously chosen time when only the day changes;
    // a fresh pick starts from the current time, not midnight.
    if (date) {
      next.setHours(date.getHours(), date.getMinutes(), 0, 0);
    } else {
      const now = new Date();
      next.setHours(now.getHours(), now.getMinutes(), 0, 0);
    }
    onChange(next.getTime());
  };

  const handleTimeChange = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const next = date ? new Date(date) : new Date();
    next.setHours(hours, minutes, 0, 0);
    onChange(next.getTime());
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('w-full justify-start text-left font-normal', !date && 'text-muted-foreground')}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP 'at' p") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={handleDateSelect} disabled={disabled} initialFocus />
        <div className="border-t p-3 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Time</span>
          <TimePicker value={timeValue} onChange={handleTimeChange} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
